
import { useState } from 'react';
import { getProductReviews, getAverageRating, getRatingDistribution } from '../../../mocks/reviews';
import type { Review } from '../../../mocks/reviews';

interface ReviewsSectionProps {
  productId: number;
}

const ReviewsSection = ({ productId }: ReviewsSectionProps) => {
  const [sortBy, setSortBy] = useState<'helpful' | 'recent'>('helpful');
  const [filterRating, setFilterRating] = useState<number | null>(null);
  const [helpfulClicked, setHelpfulClicked] = useState<number[]>([]);

  const reviews = getProductReviews(productId);
  const averageRating = getAverageRating(productId);
  const distribution = getRatingDistribution(productId);
  const totalReviews = reviews.length;

  const filteredReviews = reviews
    .filter(review => filterRating === null || review.rating === filterRating)
    .sort((a, b) => {
      if (sortBy === 'helpful') return b.helpful - a.helpful;
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    });

  const handleHelpful = (reviewId: number) => {
    if (!helpfulClicked.includes(reviewId)) {
      setHelpfulClicked(prev => [...prev, reviewId]);
    }
  };

  const renderStars = (rating: number, size: string = 'text-sm') => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map(star => (
          <i
            key={star}
            className={`${star <= rating ? 'ri-star-fill text-amber-400' : 'ri-star-line text-gray-300'} ${size}`}
          ></i>
        ))}
      </div>
    );
  };

  if (totalReviews === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8">
        <h2 className="text-xl font-bold text-gray-900 mb-6">Customer Reviews</h2>
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-chat-3-line text-3xl text-gray-400"></i>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No reviews yet</h3>
          <p className="text-gray-500">Be the first to review this product</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 lg:p-8">
      <h2 className="text-xl font-bold text-gray-900 mb-6">Customer Reviews</h2>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
        {/* Rating Summary */}
        <div className="lg:col-span-1">
          <div className="text-center lg:text-left mb-6">
            <div className="flex items-center justify-center lg:justify-start gap-3 mb-2">
              <span className="text-5xl font-bold text-gray-900">{averageRating}</span>
              <div>
                {renderStars(Math.round(averageRating), 'text-lg')}
                <p className="text-sm text-gray-500 mt-1">{totalReviews} reviews</p>
              </div>
            </div>
          </div>

          {/* Rating Distribution */}
          <div className="space-y-2">
            {[5, 4, 3, 2, 1].map(rating => {
              const count = distribution[rating];
              const percentage = totalReviews > 0 ? (count / totalReviews) * 100 : 0;
              return (
                <button
                  key={rating}
                  onClick={() => setFilterRating(filterRating === rating ? null : rating)}
                  className={`w-full flex items-center gap-3 py-1.5 px-2 rounded-lg transition-colors cursor-pointer ${
                    filterRating === rating ? 'bg-teal-50' : 'hover:bg-gray-50'
                  }`}
                >
                  <span className="text-sm text-gray-600 w-12">{rating} star</span>
                  <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-amber-400 rounded-full transition-all"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <span className="text-sm text-gray-500 w-8">{count}</span>
                </button>
              );
            })}
          </div>

          {filterRating && (
            <button
              onClick={() => setFilterRating(null)}
              className="mt-4 text-sm text-teal-600 hover:text-teal-700 cursor-pointer flex items-center gap-1"
            >
              <i className="ri-close-line"></i>
              Clear filter
            </button>
          )}
        </div>

        {/* Reviews List */}
        <div className="lg:col-span-2">
          {/* Sort Options */}
          <div className="flex items-center justify-between mb-6">
            <p className="text-sm text-gray-500">
              Showing {filteredReviews.length} of {totalReviews} reviews
            </p>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Sort by:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as 'helpful' | 'recent')}
                className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 cursor-pointer focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="helpful">Most Helpful</option>
                <option value="recent">Most Recent</option>
              </select>
            </div>
          </div>

          {/* Reviews */}
          <div className="space-y-6">
            {filteredReviews.map(review => (
              <ReviewCard
                key={review.id}
                review={review}
                onHelpful={handleHelpful}
                isHelpfulClicked={helpfulClicked.includes(review.id)}
                renderStars={renderStars}
              />
            ))}
          </div>

          {filteredReviews.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-500">No reviews match your filter</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface ReviewCardProps {
  review: Review;
  onHelpful: (id: number) => void;
  isHelpfulClicked: boolean;
  renderStars: (rating: number, size?: string) => JSX.Element;
}

const ReviewCard = ({ review, onHelpful, isHelpfulClicked, renderStars }: ReviewCardProps) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="border-b border-gray-100 pb-6 last:border-0">
      <div className="flex items-start gap-4">
        <img
          src={review.userAvatar}
          alt={review.userName}
          className="w-10 h-10 rounded-full object-cover"
        />
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-medium text-gray-900">{review.userName}</span>
            {review.verified && (
              <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                <i className="ri-checkbox-circle-fill"></i>
                Verified Purchase
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 mb-2">
            {renderStars(review.rating)}
            <span className="text-sm text-gray-400">{formatDate(review.date)}</span>
          </div>
          <h4 className="font-medium text-gray-900 mb-2">{review.title}</h4>
          <p className="text-gray-600 text-sm leading-relaxed mb-3">{review.comment}</p>

          {review.images.length > 0 && (
            <div className="flex gap-2 mb-3">
              {review.images.map((img, idx) => (
                <img
                  key={idx}
                  src={img}
                  alt="Review"
                  className="w-20 h-20 rounded-lg object-cover cursor-pointer hover:opacity-80 transition-opacity"
                />
              ))}
            </div>
          )}

          <button
            onClick={() => onHelpful(review.id)}
            disabled={isHelpfulClicked}
            className={`flex items-center gap-2 text-sm transition-colors cursor-pointer ${
              isHelpfulClicked
                ? 'text-teal-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <i className={`${isHelpfulClicked ? 'ri-thumb-up-fill' : 'ri-thumb-up-line'}`}></i>
            Helpful ({isHelpfulClicked ? review.helpful + 1 : review.helpful})
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReviewsSection;
